/**
 * Collapsible components
 * Simple collapsible components using Tailwind CSS
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'

// Context for collapsible state
interface CollapsibleContextType {
  open: boolean
  setOpen: (open: boolean) => void
}

const CollapsibleContext = createContext<CollapsibleContextType | null>(null)

// Collapsible Root
interface CollapsibleProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

export const Collapsible = React.forwardRef<HTMLDivElement, CollapsibleProps>(
  (
    { open: controlledOpen, defaultOpen = false, onOpenChange, className, children, ...props },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen)

    const open = controlledOpen !== undefined ? controlledOpen : internalOpen

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        if (controlledOpen === undefined) {
          setInternalOpen(newOpen)
        }
        onOpenChange?.(newOpen)
      },
      [controlledOpen, onOpenChange]
    )

    const contextValue = useMemo(
      () => ({ open, setOpen: handleOpenChange }),
      [open, handleOpenChange]
    )

    return (
      <CollapsibleContext.Provider value={contextValue}>
        <div ref={ref} className={cn(className)} {...props}>
          {children}
        </div>
      </CollapsibleContext.Provider>
    )
  }
)
Collapsible.displayName = 'Collapsible'

// Collapsible Trigger
interface CollapsibleTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  children: React.ReactNode
}

export const CollapsibleTrigger = React.forwardRef<HTMLButtonElement, CollapsibleTriggerProps>(
  ({ className, asChild, children, onClick, ...props }, ref) => {
    const context = useContext(CollapsibleContext)
    if (!context) throw new Error('CollapsibleTrigger must be used within Collapsible')

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      context.setOpen(!context.open)
      onClick?.(e)
    }

    // If asChild is true and children is a single React element, clone it with the click handler
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{ onClick?: React.MouseEventHandler }>,
        {
          onClick: handleClick,
        }
      )
    }

    return (
      <button ref={ref} type="button" className={cn(className)} onClick={handleClick} {...props}>
        {children}
      </button>
    )
  }
)
CollapsibleTrigger.displayName = 'CollapsibleTrigger'

// Collapsible Content
interface CollapsibleContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export const CollapsibleContent = React.forwardRef<HTMLDivElement, CollapsibleContentProps>(
  ({ className, children, ...props }, ref) => {
    const context = useContext(CollapsibleContext)
    if (!context) throw new Error('CollapsibleContent must be used within Collapsible')

    if (!context.open) return null

    return (
      <div ref={ref} className={cn('overflow-hidden', className)} {...props}>
        {children}
      </div>
    )
  }
)
CollapsibleContent.displayName = 'CollapsibleContent'
